import React from 'react';
import {
  Button,
  Icon,
  Label,
  Message,
  Popup,
  Segment,
} from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';

const matchTypeColorMap = {
  exact: 'blue',
  contains: 'orange',
  prefix: 'yellow',
  regex: 'purple',
};

const RulesTable = ({ rules, loading, onEdit, onDelete }) => {
  const { t } = useTranslation();

  const renderContent = (value, fallback = '-') => {
    const text = String(value || '').trim();
    if (!text) return fallback;
    return (
      <Popup
        content={text}
        trigger={
          <div
            style={{
              color: '#444',
              lineHeight: 1.7,
              maxHeight: '48px',
              overflow: 'hidden',
              wordBreak: 'break-word',
            }}
          >
            {text}
          </div>
        }
      />
    );
  };

  if (!loading && rules.length === 0) {
    return (
      <Message info style={{ marginTop: '8px' }}>
        <Message.Header>{t('error_mapping.table.empty')}</Message.Header>
      </Message>
    );
  }

  return (
    <Segment.Group raised>
      {rules.map((rule) => {
        const isBuiltin = String(rule.name || '').startsWith('builtin:');
        return (
          <Segment key={rule.id} style={{ padding: '18px 18px 16px 18px' }}>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '18px',
                alignItems: 'start',
              }}
            >
              <div style={{ minWidth: 0, flex: '1 1 560px' }}>
                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    marginBottom: '10px',
                  }}
                >
                  <div style={{ fontWeight: 700, color: '#1b1c1d' }}>
                    {rule.name}
                  </div>
                  <Label size='small'>#{rule.id}</Label>
                  {isBuiltin ? (
                    <Label size='small' basic color='grey'>
                      {t('error_mapping.labels.builtin', {
                        defaultValue: '内置',
                      })}
                    </Label>
                  ) : null}
                  {rule.enabled ? (
                    <Label size='small' color='green'>
                      {t('error_mapping.status.enabled')}
                    </Label>
                  ) : (
                    <Label size='small'>
                      {t('error_mapping.status.disabled')}
                    </Label>
                  )}
                </div>

                {rule.remark ? (
                  <div
                    style={{
                      color: '#777',
                      fontSize: '12px',
                      lineHeight: 1.6,
                      marginBottom: '14px',
                    }}
                  >
                    {rule.remark}
                  </div>
                ) : null}

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                    gap: '14px',
                  }}
                >
                  <div>
                    <div
                      style={{
                        color: '#888',
                        fontSize: '12px',
                        fontWeight: 600,
                        marginBottom: '6px',
                      }}
                    >
                      {t('error_mapping.table.match')}
                    </div>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      <Label basic>{t(`error_mapping.target.${rule.match_target}`)}</Label>
                      <Label color={matchTypeColorMap[rule.match_type] || 'grey'}>
                        {t(`error_mapping.match_type.${rule.match_type}`)}
                      </Label>
                    </div>
                    <div style={{ marginTop: '6px' }}>{renderContent(rule.pattern)}</div>
                  </div>

                  <div>
                    <div
                      style={{
                        color: '#888',
                        fontSize: '12px',
                        fontWeight: 600,
                        marginBottom: '6px',
                      }}
                    >
                      {t('error_mapping.table.mapping')}
                    </div>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      <Label color='blue'>{rule.mapped_code}</Label>
                    </div>
                    <div style={{ marginTop: '6px' }}>
                      {renderContent(rule.mapped_message)}
                    </div>
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                  alignItems: 'stretch',
                  flex: '0 1 220px',
                  minWidth: '180px',
                }}
              >
                <div
                  style={{
                    background: '#f8f8f8',
                    borderRadius: '8px',
                    padding: '12px',
                  }}
                >
                  <div style={{ color: '#888', fontSize: '12px', marginBottom: '6px' }}>
                    {t('error_mapping.table.priority')}
                  </div>
                  <div style={{ fontSize: '22px', fontWeight: 700 }}>{rule.priority}</div>
                </div>
                <Button size='small' primary onClick={() => onEdit(rule)}>
                  <Icon name='edit' />
                  {t('error_mapping.buttons.edit')}
                </Button>
                <Popup
                  on='click'
                  position='left center'
                  trigger={
                    <Button size='small' negative>
                      <Icon name='trash' />
                      {t('error_mapping.buttons.delete')}
                    </Button>
                  }
                  content={
                    <Button size='small' negative onClick={() => onDelete(rule)}>
                      {t('error_mapping.buttons.confirm_delete')}
                    </Button>
                  }
                />
              </div>
            </div>
          </Segment>
        );
      })}
    </Segment.Group>
  );
};

export default RulesTable;
